import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import MaintainceState from "../../components/charts/maintainceState/MaintainceState";
import WarrantyChart from "../../components/charts/warrantyChart/WarrantyChart";
import TechnicianChart from "../../components/charts/technicianChart/TechnicianChart";
import AcTypesChart from "../../components/charts/acTypesChart/AcTypesChart";
import { useReactToPrint } from "react-to-print";
import "./chartsReport.css";
import RegionChart from "../../components/charts/regionChart/RegionChart";
import MobileNav from "../../components/mobileNav/MobileNav";
import SpareParts from "../../components/charts/spareParts/SpareParts";
import printIcon from "../../assets/print-icon.svg";
import { useSelector, useDispatch } from "react-redux";
import { fetchUsers } from "../../reduxSlices/userSlice";
import PrintingHeader from "../../components/printingHeader/PrintingHeader";
import schualedIcon from "../../assets/icons/schualed-icon.svg";
import cancelIcon from "../../assets/icons/cancel-icon.png";
import AcTypesChartPage from "../acTypesChartPage/AcTypesChartPage";
import SparePartsChartPage from "../sparePartsChartPage/SparePartsChartPage";

// Fetch data from API endpoint without pagination - recommended approach
const fetchRequestData = async (url, payload) => {
  try {
    // First, get the total count
    const countPayload = {
      ...payload,
      pageNumber: 1,
      pageSize: 1,
    };

    const countResponse = await axios.post(url, countPayload);

    if (countResponse.data && Array.isArray(countResponse.data.Items)) {
      const totalItems = countResponse.data.TotalItems;

      // If no items or only a few, return from first call
      if (totalItems <= 50) {
        // Make a slightly larger request to get all items
        const itemsPayload = {
          ...payload,
          pageNumber: 1,
          pageSize: totalItems || 50,
        };

        const itemsResponse = await axios.post(url, itemsPayload);
        return itemsResponse.data?.Items || [];
      }

      // For larger datasets, use the total count
      const fullPayload = {
        ...payload,
        pageNumber: 1,
        pageSize: totalItems,
      };

      const fullResponse = await axios.post(url, fullPayload);
      return fullResponse.data?.Items || [];
    }

    return [];
  } catch (err) {
    console.error(`API Error (${url}):`, err);
    throw err;
  }
};

const ChartsReport = () => {
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Report",
    onAfterPrint: () => console.log("print success"),
  });

  const [allTechStats, setAllTechStats] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState("all"); // State for selected region
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  }); // State for date range
  const [applyDateFilter, setApplyDateFilter] = useState(false); // State to control when to apply date filter
  const [printTitle, setPrintTitle] = useState(
    "بيانات مسئولين الصيانه - الإحصائيات"
  );

  // Get all users from Redux store and dispatch function
  const users = useSelector((state) => {
    return state.users.list || [];
  });
  const dispatch = useDispatch();

  // Fetch users when component mounts
  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // Safely get maintenance area IDs from localStorage
  const getMaintenanceAreaIds = () => {
    try {
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds") || "1";
      const ids = maintenanceAreaIdsString
        .split(",")
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id));

      // If no valid IDs, use default
      if (ids.length === 0) {
        ids.push(1);
      }

      return ids;
    } catch (e) {
      console.error("Error reading maintenance areas:", e);
      return [1];
    }
  };

  const maintenanceAreaIds = getMaintenanceAreaIds();

  // Extract unique technician names from all requests
  const extractTechnicianNames = (requests) => {
    const technicianNames = new Set();

    requests.forEach((request) => {
      // Check multiple possible field names for technician name
      // Prioritize UserName since that's the key field
      const techName =
        request.UserName || // This is the primary field
        request.userName || // lowercase version
        request.TechnicianName ||
        request.technicianName ||
        request.Technician ||
        request.technician ||
        request.TechName ||
        request.techName ||
        request.FullName ||
        request.fullName;

      if (techName) {
        technicianNames.add(techName.trim()); // Trim whitespace
      }
    });

    const namesArray = Array.from(technicianNames);
    return namesArray;
  };

  // Find user data for a technician name
  const findUserData = (technicianName) => {
    // First try exact match with UserName (this is the key field)
    let user = users.find(
      (user) =>
        user.UserName === technicianName ||
        (user.UserName && user.UserName.trim() === technicianName.trim())
    );

    // If not found, try case-insensitive match with UserName
    if (!user) {
      user = users.find(
        (user) =>
          user.UserName &&
          user.UserName.toLowerCase().trim() ===
            technicianName.toLowerCase().trim()
      );
    }

    // If still not found, try other fields
    if (!user) {
      user = users.find(
        (user) =>
          user.FullName === technicianName ||
          (user.FullName && user.FullName.trim() === technicianName.trim())
      );
    }

    // If still not found, try case-insensitive match with other fields
    if (!user) {
      user = users.find(
        (user) =>
          user.FullName &&
          user.FullName.toLowerCase().trim() ===
            technicianName.toLowerCase().trim()
      );
    }

    if (user) {
      return user;
    } else {
      return {
        Id: technicianName,
        FullName: technicianName,
        Email: "",
        Image: null,
      };
    }
  };

  // Filter requests for a technician by name
  const filterRequestsByTechnicianName = (requests, techName) => {
    return requests.filter((req) => {
      const reqTechName =
        req.UserName || // This is the key field you mentioned
        req.userName || // lowercase version
        req.TechnicianName ||
        req.technicianName ||
        req.Technician ||
        req.technician ||
        req.TechName ||
        req.techName ||
        req.FullName ||
        req.fullName;

      // Compare as strings for consistency (case-insensitive and trimmed)
      return (
        reqTechName &&
        reqTechName.toString().toLowerCase().trim() ===
          techName.toLowerCase().trim()
      );
    });
  };

  // Filter requests by date range using MaintenanceStartDate
  const filterRequestsByDateRange = (requests, startDate, endDate) => {
    if (!startDate && !endDate) return requests;

    return requests.filter((req) => {
      // Use MaintenanceStartDate as the key for filtering
      const maintenanceStartDate = req.MaintenanceStartDate;

      if (!maintenanceStartDate) return false;

      try {
        // Convert any date format to Date object
        const parseDate = (dateStr) => {
          if (!dateStr) return null;

          // Handle DD-MM-YYYY format
          if (dateStr.includes("-") && dateStr.split("-")[0].length === 2) {
            const [day, month, year] = dateStr.split("-");
            return new Date(`${year}-${month}-${day}`);
          }

          // Handle YYYY-MM-DD format
          if (dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
            return new Date(dateStr.split("T")[0]);
          }

          return new Date(dateStr);
        };

        const appointmentDate = parseDate(maintenanceStartDate);
        const filterStartDate = parseDate(startDate);
        const filterEndDate = parseDate(endDate);

        // Skip if appointment date is invalid
        if (!appointmentDate || isNaN(appointmentDate.getTime())) {
          return false;
        }

        // Check if after start date (if start date is provided)
        if (filterStartDate && !isNaN(filterStartDate.getTime())) {
          // Normalize to start of day for comparison
          const startDateNormalized = new Date(filterStartDate);
          startDateNormalized.setHours(0, 0, 0, 0);
          const appointmentDateNormalized = new Date(appointmentDate);
          appointmentDateNormalized.setHours(0, 0, 0, 0);

          if (appointmentDateNormalized < startDateNormalized) {
            return false;
          }
        }

        // Check if before end date (if end date is provided)
        if (filterEndDate && !isNaN(filterEndDate.getTime())) {
          // Normalize to end of day for comparison
          const endDateNormalized = new Date(filterEndDate);
          endDateNormalized.setHours(23, 59, 59, 999);
          const appointmentDateNormalized = new Date(appointmentDate);
          appointmentDateNormalized.setHours(23, 59, 59, 999);

          if (appointmentDateNormalized > endDateNormalized) {
            return false;
          }
        }

        return true;
      } catch (e) {
        console.error("Date comparison error:", e);
        return false;
      }
    });
  };

  const fetchAllTechStats = async () => {
    try {
      setAllLoading(true);
      setError(null);

      if (!maintenanceAreaIds.length) {
        const errorMsg = "No valid maintenance areas found";
        console.error(errorMsg);
        setError("لا توجد مناطق صيانة محددة");
        return;
      }

      // Fetch all requests regardless of technician - WITHOUT PAGINATION
      const [completed, inProgress, cancelled] = await Promise.all([
        fetchRequestData(
          "https://api.generalhome.sa/api/maintenance/GetFinishedRequests",
          { maintenanceAreaIds }
        ),
        fetchRequestData(
          "https://api.generalhome.sa/api/maintenance/GetInProgressRequests",
          { maintenanceAreaIds }
        ),
        fetchRequestData(
          "https://api.generalhome.sa/api/maintenance/GetCancelRequests",
          { maintenanceAreaIds }
        ),
      ]);

      console.log("Fetched data:", {
        completed: completed.length,
        inProgress: inProgress.length,
        cancelled: cancelled.length,
      });

      // Filter ALL requests by date range if needed
      let filteredCompleted = completed;
      let filteredInProgress = inProgress;
      let filteredCancelled = cancelled;

      if (applyDateFilter && (dateRange.startDate || dateRange.endDate)) {
        filteredCompleted = filterRequestsByDateRange(
          completed,
          dateRange.startDate,
          dateRange.endDate
        );
        filteredInProgress = filterRequestsByDateRange(
          inProgress,
          dateRange.startDate,
          dateRange.endDate
        );
        filteredCancelled = filterRequestsByDateRange(
          cancelled,
          dateRange.startDate,
          dateRange.endDate
        );
      }

      // Extract unique technician names from ALL technicians
      let allTechnicianNames = extractTechnicianNames([
        ...completed,
        ...inProgress,
        ...cancelled,
      ]);

      console.log("All technician names:", allTechnicianNames);

      // Filter technicians based on their assigned areas if a specific region is selected
      if (selectedRegion !== "all") {
        allTechnicianNames = allTechnicianNames.filter((techName) => {
          // Find user data for this technician
          const techUser = findUserData(techName);

          // Check if the technician has the selected region in their Areas
          if (techUser && techUser.Areas) {
            return techUser.Areas.some(
              (area) => area.MaintenanceAreaName === selectedRegion
            );
          }

          return false;
        });
      }

      console.log("Filtered technician names:", allTechnicianNames);

      if (allTechnicianNames.length === 0) {
        setAllTechStats([]);
        return;
      }

      // For each technician, count their requests (with date filtering but without region filtering)
      const techStats = allTechnicianNames.map((techName) => {
        // Find user data for this technician
        const techUser = findUserData(techName);

        // Filter requests for this technician from the DATE-FILTERED requests
        const techCompleted = filterRequestsByTechnicianName(
          filteredCompleted,
          techName
        );
        const techInProgress = filterRequestsByTechnicianName(
          filteredInProgress,
          techName
        );
        const techCancelled = filterRequestsByTechnicianName(
          filteredCancelled,
          techName
        );

        const stats = {
          CompletedRequests: techCompleted.length,
          InProgressRequests: techInProgress.length,
          CancelledRequests: techCancelled.length,
          TotalRequests:
            techCompleted.length + techInProgress.length + techCancelled.length,
        };

        console.log(`Tech ${techName} stats:`, stats);

        return {
          tech: techUser,
          stats: stats,
        };
      });

      setAllTechStats(techStats);
    } catch (err) {
      console.error("Error fetching all technicians:", err);
      setError("فشل تحميل بيانات الفنيين");
    } finally {
      setAllLoading(false);
    }
  };

  // Load stats when component mounts and when users are loaded
  useEffect(() => {
    if (maintenanceAreaIds.length > 0) {
      fetchAllTechStats();
    } else {
      console.log(
        "Conditions not met - maintenanceAreaIds.length:",
        maintenanceAreaIds.length
      );
    }
  }, [
    maintenanceAreaIds.toString(),
    users.length,
    selectedRegion,
    applyDateFilter,
  ]); // Changed to applyDateFilter

  // Function to handle image errors properly
  const handleImageError = (e) => {
    // Prevent infinite loop by setting a data attribute
    if (!e.target.getAttribute("data-error-handled")) {
      e.target.setAttribute("data-error-handled", "true");
      // Use a base64 encoded placeholder image to avoid external requests
      e.target.src =
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiIGZpbGw9IiNFM0UzRTMiLz4KPHBhdGggZD0iTTQwIDQ0QzQzLjMxMzcgNDQgNDYgNDEuMzEzNyA0NiAzOEM0NiAzNC42ODYzIDQzLjMxMzcgMzIgNDAgMzJDMzYuNjg2MyAzMiAzNCAzNC42ODYzIDM0IDM4QzM0IDQxLjMxMzcgMzYuNjg2MyA0NCA0MCA0NFoiIGZpbGw9IiM5OTk5OTkiLz4KPHBhdGggZD0iTTQ4LjUgMzQuNUw1My41IDI5LjVMNTYgMzJMNTAuNSAzNy41TDQ4LjUgMzQuNVoiIGZpbGw9IiM5OTk5OTkiLz4KPHBhdGggZD0iTTMxLjUgMzQuNUwyNi41IDI5LjVMMjQgMzJMMjkuNSAzNy41TDMxLjUgMzQuNVoiIGZpbGw9IiM5OTk5OTkiLz4KPC9zdmc+";
    }
  };

  const desiredOrder = [
    "محمد يونس",
    "محمد محسن",
    "محمد عثمان",
    "علي سليم",
    "محمد أنيس",
    "ساروش خالد",
    "نديم",
    "فضل أحمد",
  ];

  const sortTechnicians = (techStats) => {
    return [...techStats].sort((a, b) => {
      const nameA = a.tech.FullName || "";
      const nameB = b.tech.FullName || "";

      const indexA = desiredOrder.indexOf(nameA);
      const indexB = desiredOrder.indexOf(nameB);

      // If both names are in the desired order array, sort by their index
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only nameA is in the desired order, it should come first
      if (indexA !== -1) return -1;

      // If only nameB is in the desired order, it should come first
      if (indexB !== -1) return 1;

      // If neither name is in the desired order, maintain original order
      return 0;
    });
  };

  // Function to filter out inactive technicians
  const filterActiveTechnicians = (techStats) => {
    return techStats.filter(({ tech }) => {
      // Check if the user is active
      // Use both IsActive and isActive for case sensitivity
      const isActive =
        tech.IsActive !== undefined
          ? tech.IsActive
          : tech.isActive !== undefined
          ? tech.isActive
          : true;

      // Only include active technicians (default to true if property doesn't exist)
      return isActive !== false;
    });
  };

  const activeTechStats = filterActiveTechnicians(allTechStats);

  // Get sorted technician stats
  const sortedTechStats = sortTechnicians(activeTechStats);

  // Handle date range changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Clear date filters
  const clearDateFilters = () => {
    setDateRange({
      startDate: "",
      endDate: "",
    });
    setApplyDateFilter(false); // Reset the filter application
  };

  // Handle search button click
  const handleSearch = () => {
    setApplyDateFilter(true); // Trigger the date filter application
  };

  // Listen for Bootstrap tab changes
  useEffect(() => {
    const handleTabChange = (e) => {
      const targetId = e.target.getAttribute("data-bs-target");
      switch (targetId) {
        case "#pills-charts":
          setPrintTitle("الإحصائيات");
          break;
        case "#pills-other":
          setPrintTitle("أعمال الفنيين");
          break;
        case "#pills-ac-types":
          setPrintTitle("إحصائيات أنواع المكيفات");
          break;
        case "#pills-parts":
          setPrintTitle("إحصائيات قطع الغيار");
          break;
        default:
          setPrintTitle("بيانات مسئولين الصيانه");
      }
    };

    // Add event listeners to all tab buttons
    const tabButtons = document.querySelectorAll('[data-bs-toggle="pill"]');
    tabButtons.forEach((button) => {
      button.addEventListener("shown.bs.tab", handleTabChange);
    });

    // Cleanup
    return () => {
      tabButtons.forEach((button) => {
        button.removeEventListener("shown.bs.tab", handleTabChange);
      });
    };
  }, []);

  return (
    <>
      <MobileNav />

      {/* ===== Chart Header ===== */}
      <div className="chartsHeder d-flex justify-content-between mt-3 mb-4">
        {/* ===== Tabs ===== */}
        <ul
          className="nav nav-pills container g-3"
          id="pills-tab"
          role="tablist"
        >
          <li className="nav-item" role="presentation">
            <button
              className="nav-link active"
              id="pills-charts-tab"
              data-bs-toggle="pill"
              data-bs-target="#pills-charts"
              type="button"
              role="tab"
              aria-controls="pills-charts"
              aria-selected="true"
            >
              الإحصائات
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className="nav-link"
              id="pills-other-tab"
              data-bs-toggle="pill"
              data-bs-target="#pills-other"
              type="button"
              role="tab"
              aria-controls="pills-other"
              aria-selected="false"
            >
              أعمال الفنيين
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className="nav-link"
              id="pills-ac-types-tab"
              data-bs-toggle="pill"
              data-bs-target="#pills-ac-types"
              type="button"
              role="tab"
              aria-controls="pills-ac-types"
              aria-selected="false"
            >
              أنواع المكيفات
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className="nav-link"
              id="pills-parts-tab"
              data-bs-toggle="pill"
              data-bs-target="#pills-parts"
              type="button"
              role="tab"
              aria-controls="pills-parts"
              aria-selected="false"
            >
              قطع الغيار
            </button>
          </li>
        </ul>
        {/* ===== print-icon ===== */}
        <div className="print-icon-holder">
          <img
            loading="lazy"
            src={printIcon}
            alt="printIcon"
            className="print-icon"
            onClick={handlePrint}
          />
        </div>
      </div>

      {/* ===== Tabs Content ===== */}
      <div
        className="tab-content chart-tabs-container"
        id="pills-tabContent"
        ref={componentRef}
      >
        <PrintingHeader
          title={printTitle}
          selectedRegion={selectedRegion}
          showCount={false} // Hide count
          showRegion={true} // Show region
        />

        {(dateRange.startDate || dateRange.endDate) && (
          <div className="date-range-display">
            <div className="date-range-item">
              <span className="date-label">من</span>
              <span className="date-value">{dateRange.startDate || "--"}</span>
            </div>
            <div className="date-range-item">
              <span className="date-label">إلى</span>
              <span className="date-value">{dateRange.endDate || "--"}</span>
            </div>
          </div>
        )}
        {/* First Tab - Charts Report */}
        <div
          className="tab-pane fade show active"
          id="pills-charts"
          role="tabpanel"
          aria-labelledby="pills-charts-tab"
          tabIndex="0"
        >
          <div className="chartsReport">
            <div className="chartsReport-container container">
              <div className="row g-4">
                <div className="col-lg-6">
                  <WarrantyChart />
                </div>
                <div className="col-lg-6">
                  <MaintainceState />
                </div>
                <div className="col-lg-6">
                  <div className="row g-4">
                    <div className="col-lg-12">
                      <RegionChart />
                    </div>
                    {/* <div className="col-lg-12">
                      <TechnicianChart />
                    </div> */}
                  </div>
                </div>
                {/* <div className="col-lg-6">
                  <AcTypesChart />
                </div> */}
                {/* <div className="">
                  <SpareParts />
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Second Tab - All Technicians */}
        <div
          className="tab-pane fade"
          id="pills-other"
          role="tabpanel"
          aria-labelledby="pills-other-tab"
          tabIndex="0"
        >
          <div className="contaainer">
            {/* Region Filter Dropdown */}
            <div className="row mb-4">
              <div className="col-md-4">
                <select
                  id="regionFilter"
                  className="form-select"
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                >
                  <option value="all">جميع المناطق</option>
                  <option value="الرياض">الرياض</option>
                  <option value="القصيم">القصيم</option>
                </select>
              </div>
              {/* Date Range Filters */}
              <div className="col-md-8">
                <div className="row g-2">
                  <div className="col">
                    <input
                      type="date"
                      className="form-control"
                      name="startDate"
                      value={dateRange.startDate}
                      onChange={handleDateChange}
                      placeholder="من تاريخ"
                    />
                  </div>
                  <div className="col">
                    <input
                      type="date"
                      className="form-control"
                      name="endDate"
                      value={dateRange.endDate}
                      onChange={handleDateChange}
                      placeholder="إلى تاريخ"
                    />
                  </div>
                  <div className="col-auto">
                    <button className="btn btn-primary" onClick={handleSearch}>
                      بحث
                    </button>
                  </div>
                  <div className="col-auto">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={clearDateFilters}
                    >
                      مسح
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {allLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">جاري التحميل...</span>
                </div>
                <p className="mt-3">جاري تحميل بيانات الفنيين...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger text-center" role="alert">
                {error}
              </div>
            ) : sortedTechStats.length === 0 ? (
              <div className="alert alert-info text-center" role="alert">
                لا توجد بيانات للفنيين
              </div>
            ) : (
              <div className="row">
                {sortedTechStats.map(({ tech, stats }) => (
                  <div key={tech.Id || tech.FullName} className=" mb-2">
                    <div className="stat-card-container">
                      <div className="card-body">
                        {/* User Info Section */}
                        <div className="user-info-card mb-4">
                          <div className="user-info-header">
                            {tech.Image ? (
                              <img
                                loading={"lazy"}
                                src={tech.Image}
                                alt={tech.FullName}
                                className="rounded-circle"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                }}
                                onError={handleImageError}
                              />
                            ) : (
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  backgroundColor: "#E3E3E3",
                                  color: "#999",
                                  fontSize: "24px",
                                }}
                              >
                                {tech.FullName ? tech.FullName.charAt(0) : "T"}
                              </div>
                            )}
                          </div>
                          <div className="user-info-body text-center">
                            <div className="user-info-row">
                              <span className="user-info-value">
                                {tech.FullName || "غير متوفر"}
                              </span>
                            </div>
                            <div className="user-info-row">
                              <p className="user-info-value small text-muted">
                                {tech.Email || ""}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Stats Cards */}
                        <div className="stat-card active">
                          <div className="stat-card-header">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="25"
                              height="24"
                              viewBox="0 0 25 24"
                              fill="none"
                            >
                              <path
                                d="M11.5476 21.5239H23.9286"
                                stroke="#16A34A"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M11.5476 12.9526H23.9286"
                                stroke="#16A34A"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M11.5476 4.38086H23.9286"
                                stroke="#16A34A"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                opacity="0.6"
                                d="M1.07141 4.06361L2.49998 5.33345L6.7857 1.52393"
                                stroke="#16A34A"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                opacity="0.6"
                                d="M1.07141 12.6349L2.49998 13.9047L6.7857 10.0952"
                                stroke="#16A34A"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                opacity="0.6"
                                d="M1.07141 21.2067L2.49998 22.4765L6.7857 18.667"
                                stroke="#16A34A"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <h3 className="doneReq">الطلبات المكتملة</h3>
                          </div>
                          <div className="stat-number">
                            {stats.CompletedRequests}
                          </div>
                        </div>

                        <div className="stat-card active">
                          <div className="stat-card-header">
                            <img
                              loading={"lazy"}
                              src={schualedIcon}
                              alt="in progress"
                            />
                            <h3 className="schiueledReq">قيد التنفيذ</h3>
                          </div>
                          <div className="stat-number">
                            {stats.InProgressRequests}
                          </div>
                        </div>

                        <div className="stat-card active">
                          <div className="stat-card-header">
                            <img
                              src={cancelIcon}
                              alt="cancelled"
                              loading={"lazy"}
                            />
                            <h3 className="cancelReq">الطلبات الملغيه</h3>
                          </div>
                          <div className="stat-number">
                            {stats.CancelledRequests}
                          </div>
                        </div>

                        <div className="stat-card active">
                          <div className="stat-card-header">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                              fill="none"
                            >
                              <path
                                d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z"
                                fill="#0D9AE1"
                                fillOpacity="0.16"
                              />
                              <path
                                d="M9.8378 16.7117L13.4949 19.977L22.0283 12.3579"
                                stroke="#0D9AE1"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <h3 className="totalReq">إجمالي الطلبات</h3>
                          </div>
                          <div className="stat-number">
                            {stats.TotalRequests}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Third Tab - All Technicians */}
        <div
          className="tab-pane fade"
          id="pills-ac-types"
          role="tabpanel"
          aria-labelledby="pills-ac-types-tab"
          tabIndex="0"
        >
          <AcTypesChartPage />
        </div>
        <div
          className="tab-pane fade"
          id="pills-parts"
          role="tabpanel"
          aria-labelledby="pills-parts-tab"
          tabIndex="0"
        >
          <SparePartsChartPage />
        </div>

        <div className="tech-signiture">
          <h5>توقيع المسؤول</h5>
          <p>....................................</p>
        </div>
      </div>
    </>
  );
};

export default ChartsReport;
